import React, { useState } from "react";
import { Button, IconButton, Paper, TextField } from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { makeStyles } from "@material-ui/core/styles";
import { authDelete, authPost } from "../../../lib";

const useStyles = makeStyles((theme) => ({
  panel: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  title: {
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".02em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  add: { display: "flex", gap: theme.spacing(1), alignItems: "flex-start" },
  field: { flex: 1 },
  list: { marginTop: theme.spacing(1.5) },
  entry: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: "1px solid rgba(0,0,0,0.07)",
    fontSize: 13,
    lineHeight: 1.6,
    "&:last-child": { borderBottom: "none" },
  },
  body: { flex: 1 },
  stamp: { color: theme.palette.text.secondary, fontWeight: 600 },
  del: { padding: 2, color: "rgba(0,0,0,0.3)", "&:hover": { color: "#c62828" } },
  empty: { color: theme.palette.text.secondary, fontSize: 13 },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
}));

/**
 * Internal admin notes, as the legacy repair and return screens had them:
 * newest first, each stamped with who wrote it, individually deletable.
 *
 * These are staff-only — nothing here is shown to or emailed to a customer,
 * which is why the box is plain and the entries are terse.
 *
 * @param {string} endpoint  e.g. "/repairs/4250" — "/notes" is appended
 */
export function AdminNotesCard({ endpoint, notes, onChange, title = "Internal Admin Notes" }) {
  const classes = useStyles();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const add = async (e) => {
    // Without this the form submit reloads and throws the page back to the top.
    e?.preventDefault?.();
    const note = draft.trim();
    if (!note) return;

    setBusy(true);
    setError("");
    const { data, error: err } = await authPost(`${endpoint}/notes`, { note });
    setBusy(false);

    if (err) {
      setError(err.message || "Could not add that note.");
      return;
    }
    setDraft("");
    onChange(data.notes);
  };

  const remove = (n, index) => async () => {
    // Both legacy screens confirm first, and a note cannot be undeleted.
    // eslint-disable-next-line no-alert
    if (!window.confirm("Are you sure you want to delete this note?")) return;

    setBusy(true);
    setError("");
    // Position identifies the note; the stamp is a check that the list has not
    // shifted since it was rendered.
    const query = `${endpoint}/notes?index=${index}` +
      (n.datetime ? `&datetime=${encodeURIComponent(n.datetime)}` : "");
    const { data, error: err } = await authDelete(query);
    setBusy(false);

    if (err) {
      setError(err.message || "Could not delete that note.");
      return;
    }
    onChange(data.notes);
  };

  return (
    <Paper className={classes.panel}>
      <div className={classes.title}>{title}</div>

      <form onSubmit={add} className={classes.add}>
        <TextField
          className={classes.field}
          multiline
          rows={2}
          placeholder="Add an internal note…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
        />
        <Button
          type="submit"
          size="small"
          variant="contained"
          color="primary"
          disabled={busy || !draft.trim()}
        >
          Add Note
        </Button>
      </form>

      {error && <div className={classes.error}>{error}</div>}

      <div className={classes.list}>
        {!notes?.length ? (
          <div className={classes.empty}>No notes yet.</div>
        ) : (
          notes.map((n, i) => (
            <div className={classes.entry} key={`${n.datetime}-${i}`}>
              <span className={classes.body}>
                <span className={classes.stamp}>
                  {n.datetime}
                  {n.user ? ` - ${n.user}` : ""}:
                </span>{" "}
                {n.note}
              </span>
              <IconButton
                size="small"
                className={classes.del}
                title="Delete this note"
                onClick={remove(n, i)}
                disabled={busy}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          ))
        )}
      </div>
    </Paper>
  );
}

export default AdminNotesCard;
